import type { IndividualMetric } from '@dashhudson/api-client/dashboard/model/raw';

import camelCase from 'lodash/camelCase';
import isEqual from 'lodash/isEqual';
import isNumber from 'lodash/isNumber';
import numeral from 'numeral';

import {
  BAR_CHART_REPORTS,
  getReportConfigProp,
  METRIC_TYPE_REPORT_KEYS,
  REPORT_CATEGORY_OPTIONS,
} from '@/app/dashboards/utils/reports.enum';
import constants from '@/app/settings/components/CustomMetrics/constants';
import { toolTips } from '@/config';
import { ADS_REPORT_AGGREGATE_BY_TYPES } from '@/models/dashboards/aggregate-type.enum';
import {
  ADS_CHANNELS,
  CHANNELS,
  MULTI_CHANNEL_CONTENT_KEY,
  MULTI_CHANNEL_KEY,
} from '@/models/dashboards/channels.enum';
import { GRAPH_SCALES } from '@/models/dashboards/graph-scales.enum';
import { GRAPH_STATS } from '@/models/dashboards/graph-stats.enum';
import { DURATION_FORMATS, POST_TYPES } from '@/models/dashboards/metrics.constants';
import { getEnumValue } from '@/models/enum.utils';
import { useDashboardReportsStore } from '@/stores/dashboards-reports';
import { useFlagStore } from '@/stores/flag';
import { formatDuration, formatDynamicDuration } from '@/utils/formatters';
import { getMetricDetails } from '@/utils/metrics';

/** A single metric's config as returned by the metric-details API. */
type MetricDetail = IndividualMetric;
/**
 * Nested `channel → reportKey → metric → detail` lookup. Multi-channel comparable reports store
 * their entries directly under the `MULTI_CHANNEL*` keys at the top level (a 2-level slice), read
 * via a cast in {@link filterMetricsIfHidden}.
 */
type MetricDetailsRegistry = Record<string, Record<string, Record<string, MetricDetail>>>;
type MultiChannelMetricDetails = Record<string, Record<string, MetricDetail>>;
/** `metric → channel → mapped metric key` for multi-channel comparable reports. */
type ComparableMetrics = Record<string, Record<string, string>>;
/** How a metric value arrives before formatting. */
type MetricValue = number | string | null | undefined;
type MetricPredicate = (metric: string) => boolean;

const multiChannelTooltips: Record<string, string> = {
  ...toolTips.multiChannelDashboard,
  ...toolTips.sentimentDashboard,
};

export const DEFAULT_FORMAT = Object.freeze({
  NORMAL: {
    value: 'NORMAL',
    format: '0.[00]a',
  },
  LONG: {
    value: 'LONG',
    format: '0,0.[00]',
  },
});

export function getMediaV2MetricName(_channel: string, metric: string): string {
  const metricValue = getEnumValue(metric);
  return metricValue?.toLowerCase() || metricValue;
}

export function isMultiChannel(channels: unknown): boolean {
  return Array.isArray(channels) && channels.length > 1;
}

export function getMetricText(
  firstChannel: string,
  metric: string,
  allChannels: string[] | null = null,
  report: unknown = null,
  aggregatedBy: string = '',
  metricReportTypeKey: string | null = null,
): string | null | undefined {
  const metricTypeReportKey =
    metricReportTypeKey ?? getReportConfigProp(report, 'metricTypeReportKey');
  const channels = allChannels ?? [firstChannel];
  let { displayName: name } = getMetricDetails(metric, channels, metricTypeReportKey);
  if (!ADS_CHANNELS.includes(firstChannel)) {
    return name;
  }
  if (
    [CHANNELS.META_ADS.value, CHANNELS.TIKTOK_ADS.value].includes(firstChannel) &&
    name &&
    aggregatedBy
  ) {
    const aggregateType = (ADS_REPORT_AGGREGATE_BY_TYPES as Record<string, { text: string }>)[
      aggregatedBy
    ];
    name = name.replace('- Ads', `${aggregateType.text} - Ads`);
  }
  return name;
}

export function getMetricTooltip(channels: string[], metric: string): string {
  if (isMultiChannel(channels)) {
    return multiChannelTooltips[camelCase(metric)] ?? '';
  }
  return '';
}

export function getMetricGraphScale(): string[] {
  return Object.keys(GRAPH_SCALES);
}

export function getMetricGraphStats(
  _channel: string,
  _metric: string,
  reportType: string | null = null,
  metricType: string | null = null,
  isCustomMetric: boolean = false,
): string[] {
  if (metricType === constants.METRIC_TYPE.PERCENTAGE.value && isCustomMetric) {
    return [];
  }
  if (BAR_CHART_REPORTS.includes(reportType as string)) {
    return [GRAPH_STATS.SUM.value];
  }
  return [GRAPH_STATS.SUM.value, GRAPH_STATS.MEAN_BY_DAY.value];
}

function formatValue(
  value: MetricValue,
  format: string = DEFAULT_FORMAT.NORMAL.format,
  emptyValue: string | undefined = undefined,
): string | undefined {
  switch (format) {
    case DURATION_FORMATS.HOURS:
      return formatDuration(value, 0, 'hour');
    case DURATION_FORMATS.HOURS_MINUTES:
      return formatDuration(value, 0, 'minute');
    case DURATION_FORMATS.HOURS_MINUTES_SECONDS:
      return formatDuration(value, (value as number) >= 60 ? 0 : 1);
    case DURATION_FORMATS.SHORTENED_YEARS_DAYS_HOURS_MINUTES_SECONDS:
      return formatDynamicDuration(value);
    default:
      return isNumber(value) ? numeral(value).format(format).toUpperCase() : emptyValue;
  }
}

export function formatMetric(
  _channel: string,
  _metric: string,
  value: MetricValue,
  type: string | { value: string } | null | undefined,
  unit: string = '',
): string {
  const formatType = getEnumValue(type);
  const format =
    DEFAULT_FORMAT[formatType as keyof typeof DEFAULT_FORMAT]?.format ||
    DEFAULT_FORMAT.NORMAL.format;
  let formattedValue = formatValue(value, format, '-') ?? '-';
  if (unit) {
    formattedValue = formattedValue.concat(` ${unit}`);
  }
  return formattedValue;
}

export function filterMetricsIfHidden(
  channels: string[],
  allMetricDetails: MetricDetailsRegistry | null | undefined,
  metricTypeReportKey: string,
): MetricPredicate {
  return (metric) => {
    const flagStore = useFlagStore();
    const hasRequiredFlag = (requiredFlag: string | null | undefined) =>
      !requiredFlag || (flagStore.ready && flagStore.flags?.[requiredFlag]);
    const isMultipleChannels = channels.length > 1;

    // For multi-channel comparable reports, defer to the MULTI_CHANNEL entry for
    // required_feature_flag and hide_on_dashboards. Per-channel metrics reached
    // via the comparable map may be intentionally hidden for single-channel reports
    // but still valid when surfaced through a comparable.
    if (isMultipleChannels) {
      let comparableKey = MULTI_CHANNEL_KEY;
      if (metricTypeReportKey === METRIC_TYPE_REPORT_KEYS.CONTENT_REPORT)
        comparableKey = MULTI_CHANNEL_CONTENT_KEY;
      // Multi-channel entries live one level shallower than the per-channel registry.
      const comparableDetails = (
        allMetricDetails as MultiChannelMetricDetails | null | undefined
      )?.[comparableKey]?.[metric];
      if (!hasRequiredFlag(comparableDetails?.required_feature_flag)) return false;
      if (comparableDetails?.hide_on_dashboards) return false;
      return true;
    }

    return channels.every((channel) => {
      const metricDetails = allMetricDetails?.[channel]?.[metricTypeReportKey]?.[metric];
      if (!hasRequiredFlag(metricDetails?.required_feature_flag)) return false;
      return !metricDetails?.hide_on_dashboards;
    });
  };
}

export function filterMetricIfUnknownInChannel(
  channels: string[],
  comparableMetrics: ComparableMetrics | null | undefined,
  allMetricDetails: MetricDetailsRegistry | null | undefined,
  metricTypeReportKey: string,
): MetricPredicate {
  const isMultiChannelReport = channels.length > 1;
  return (metric) =>
    channels.every((channel) => {
      const mappedMetric = isMultiChannelReport ? comparableMetrics?.[metric]?.[channel] : metric;
      if (!mappedMetric) return false;

      if (
        channel === CHANNELS.INSTAGRAM.value &&
        ![
          'AVG_EFFECTIVENESS_V3',
          'IMPRESSIONS_V2',
          'TOTAL_VIDEO_VIEWS_V2',
          'ORGANIC_VIDEO_VIEWS_V2',
        ].includes(metric)
      ) {
        return Boolean(
          allMetricDetails?.[channel]?.[metricTypeReportKey]?.[mappedMetric] ||
            allMetricDetails?.[CHANNELS.INSTAGRAM_UGC.value]?.[metricTypeReportKey]?.[mappedMetric],
        );
      }
      return Boolean(allMetricDetails?.[channel]?.[metricTypeReportKey]?.[mappedMetric]);
    });
}

export function metricSupportsContentTags(
  channel: string,
  metrics: string | string[] | null | undefined,
  report: unknown,
  allMetricDetails: MetricDetailsRegistry | null | undefined,
): boolean {
  const metricTypeReportKey = getReportConfigProp(report, 'metricTypeReportKey');

  const metricList: string[] = ([] as string[]).concat(metrics ?? []);
  return metricList.every((metric) =>
    Boolean(allMetricDetails?.[channel]?.[metricTypeReportKey ?? '']?.[metric]?.allow_content_tags),
  );
}

export function filterMetricsByDataSource(
  channels: string[],
  allMetricDetails: MetricDetailsRegistry | null | undefined,
  comparableMetrics: ComparableMetrics | null | undefined,
  metricTypeReportKey: string,
  reportCategory: string | null | undefined,
): MetricPredicate {
  return (metric) => {
    const isMultiChannelReport = channels.length > 1;

    if (!reportCategory) {
      return true;
    }

    const categoryConfig = Object.values(REPORT_CATEGORY_OPTIONS).find(
      (category) => category?.id === reportCategory,
    );

    if (!categoryConfig?.dataSources) {
      return true;
    }

    return channels.every((channel) => {
      const mappedMetric = isMultiChannelReport ? comparableMetrics?.[metric]?.[channel] : metric;
      const metricDetails =
        allMetricDetails?.[channel]?.[metricTypeReportKey]?.[mappedMetric ?? ''];
      const dataSource = metricDetails?.data_source;

      return categoryConfig.dataSources.includes(dataSource as string);
    });
  };
}

export function metricRequiresAdAccount(
  metricDetails: MetricDetail | null | undefined,
  channel: string | null = null,
): boolean {
  if (channel != null && ADS_CHANNELS.includes(channel)) return false;

  const postTypes = metricDetails?.post_types ?? [];
  if (postTypes.length === 0) return false;

  // Promoted-only or ADS-only metrics require ad account for all channels
  const isPromotedOrAdsOnly = postTypes.every((postType) =>
    ([POST_TYPES.PROMOTED, POST_TYPES.ADS] as string[]).includes(postType),
  );
  if (isPromotedOrAdsOnly) return true;

  const isEq = (postTypes1: string[], postTypes2: string[]) =>
    isEqual(new Set(postTypes1), new Set(postTypes2));
  if (channel === CHANNELS.TIKTOK.value) {
    return (
      isEq(postTypes, [POST_TYPES.ORGANIC]) ||
      isEq(postTypes, [POST_TYPES.PROMOTED]) ||
      isEq(postTypes, [POST_TYPES.ESTIMATED_ORGANIC])
    );
  }

  if (channel === CHANNELS.FACEBOOK.value) {
    return isEq(postTypes, [POST_TYPES.ORGANIC]) || isEq(postTypes, [POST_TYPES.PROMOTED]);
  }

  if (channel === CHANNELS.INSTAGRAM.value) {
    return (
      isEq(postTypes, [POST_TYPES.PROMOTED]) ||
      isEq(postTypes, [POST_TYPES.ORGANIC, POST_TYPES.PROMOTED])
    );
  }

  return false;
}

export function metricIsOrganicAndPromoted(
  metricDetails: MetricDetail | null | undefined,
): boolean {
  const postTypes = metricDetails?.post_types ?? [];
  const isEq = (a: string[], b: string[]) => isEqual(new Set(a), new Set(b));
  return isEq(postTypes, [POST_TYPES.ORGANIC, POST_TYPES.PROMOTED]);
}

export function filterMetricsByAdAccountConnection(
  channels: string[],
  allMetricDetails: MetricDetailsRegistry | null | undefined,
  comparableMetrics: ComparableMetrics | null | undefined,
  metricTypeReportKey: string,
  hasAdAccountForChannel: (channel: string) => boolean,
): MetricPredicate {
  return (metric) => {
    const isMultiChannelReport = channels.length > 1;

    return channels.every((channel) => {
      const mappedMetric = isMultiChannelReport ? comparableMetrics?.[metric]?.[channel] : metric;
      const metricDetails =
        allMetricDetails?.[channel]?.[metricTypeReportKey]?.[mappedMetric ?? ''];

      const requiresAdAccount = metricRequiresAdAccount(metricDetails, channel);
      if (!requiresAdAccount) return true;

      return hasAdAccountForChannel(channel);
    });
  };
}

export function filterMetricsIfContentTags(
  channels: string[],
  contentTagIds: Array<string | number> | null | undefined,
  report: unknown,
  allMetricDetails: MetricDetailsRegistry | null | undefined,
  isReportForm: boolean,
): MetricPredicate {
  const dashboardReportsStore = useDashboardReportsStore();
  const hasContentTags = (contentTagIds?.length ?? 0) > 0;

  return (metric) => {
    if (!hasContentTags || isReportForm) return true;
    return channels.every((channel) => {
      const comparableMetric =
        channels.length > 1
          ? dashboardReportsStore.getComparableMetricBasedOnChannel(channel, metric)
          : metric;

      return metricSupportsContentTags(channel, comparableMetric, report, allMetricDetails);
    });
  };
}
